import React from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'

import { Button, ButtonProps } from '@acorns/web-components'
import styled from 'styled-components'

import InfoIconSVG from 'assets/icons/info-white.svg'
import LockSVG from 'assets/icons/lock.svg'
import TextInput from 'components/Form/TextInput'
import { withTrackClicked } from 'components/trackers'
import { Bodytext } from 'components/typography'
import { style } from 'theme'

export type WebViewProps = {
  handleWebViewBack: () => void
  handleWebViewExit: () => void
  mobileUserUuid?: string
  mobileSessionId?: string
  location?: any
}

export const Container = styled.div`
  margin: 0 auto;
  position: relative;
  min-height: 100%;
  background: rgb(245, 245, 245);
`

export const BackArrow = styled.img`
  opacity: 0.5;
  width: 16px;
`

export const LockedTextInput = styled(TextInput)`
  img {
    display: none;
  }
`

export const SSNHint = styled.div`
  color: ${style((theme) => theme.colors.stone)};
  font-size: 12px;
  line-height: 2;
  padding: 10px 20px;
`

export const LockIcon = () => <LockIconImg src={LockSVG} />

export const LockIconImg = styled.img`
  position: absolute;
  right: 20px;
  width: 8px;
  margin-top: 5px;
`

export const InfoBlock = styled.div`
  padding: 30px 50px;
  background: #ffffff;
  font-size: 14px;
  line-height: 19px;
  color: ${style((theme) => theme.colors.stone)};
  text-align: center;
`

export const SectionTitle = styled.div`
  padding: 25px 20px 10px;
  font-size: 14px;
  line-height: 19px;
  color: ${style((theme) => theme.colors.slate)};
  text-align: left;
  font-weight: 600;
`

export const FieldsContainer = styled.div`
  background: #ffffff;
  padding: 15px 20px;

  &:last-child {
    padding-bottom: 0;
  }

  input {
    font-size: 14px;
    line-height: 32px;
    font-weight: 400;
    color: ${style((theme) => theme.colors.slate)};
    border: none;
    padding: 0;
    margin-bottom: -15px;
    margin-top: -10px;

    &:disabled {
      background-color: unset;
    }
  }
`

export const FormContainer = styled<{ padding: string }, any>(FieldsContainer)`
  padding: ${(props) => (props.padding ? props.padding : '0')};

  .web-view-input {
    padding: 15px 20px 20px;

    &.last-row {
      &.last-row-left {
        padding-right: 0;

        label:last-child {
          right: 15px;
        }
      }
      &.last-row-right {
        padding-left: 0;
      }
    }

    &:not(.last-row) {
      border-bottom: 1px solid #f5f5f5;
    }

    label:first-child {
      font-size: 12px;
      font-weight: normal;
      color: ${style((theme) => theme.colors.stone)};
    }

    label:last-child {
      left: unset;
      right: 20px;
      top: 11px;
    }

    select {
      padding: 0;
      height: auto;
      border: unset;
    }
  }
`

export const FieldRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;
  padding-bottom: 15px;

  &:last-child {
    padding-bottom: 0;
  }
`

export const Field = styled.div`
  display: block;
  flex-basis: 100%;
  max-width: 100%;
  padding-bottom: 10px;

  &:last-child {
    padding-bottom: 0;
  }
`

export const FieldLabel = styled.label`
  color: ${style((theme) => theme.colors.stone)};
  font-size: 12px;
  font-weight: normal;
  line-height: 25px;
  margin-bottom: 6px;
  width: 100%;
`

export const FieldName = styled.div`
  color: ${style((theme) => theme.colors.stone)};
  font-size: 12px;
  font-weight: normal;
  line-height: 25px;
  margin-bottom: 6px;
  width: 100%;
`

export const FieldValue = styled.div`
  color: ${style((theme) => theme.colors.slate)};
  font-size: 14px;
  line-height: 20px;
`

export const GreenText = styled.span`
  color: #51cc26;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`

export const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column-reverse;
  padding: 20px;
`

export const StyledButton = styled(Button)`
  &&& {
    margin-bottom: 20px;
    width: 100%;
  }
`

export const Panel = styled.div`
  color: ${style((theme) => theme.colors.slate)};
  background-color: #ffffff;
  border-radius: ${style((theme) => theme.styles.borderRadius)};
  box-shadow: ${style((theme) => theme.shadows.smMedium)};
  font-size: 14px;
  font-weight: normal;
  margin: 20px;
  padding: 20px;
  position: relative;
`

export const Title = styled.h2`
  color: ${style((theme) => theme.colors.slate)};
  margin: 0 0 20px;
  font-size: 22px;
  font-weight: 500;
  line-height: 28px;
`

export const PanelTitle = styled.div`
  margin: 20px 20px -10px;
  display: flex;
`

export const PanelTitleText = styled.span`
  color: ${style((theme) => theme.colors.stone)};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  flex: 1;
`

export const PanelTitleLink = styled.span`
  color: ${style((theme) => theme.colors.stone)};
  cursor: pointer;
  border: none;
  flex: 1;
  padding: 0;
  justify-content: flex-end;
  height: auto;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  text-align: right;
`

export const InstructionsList = styled.ol`
  margin: 30px 0 0;
  padding-left: 15px;
`

export const InstructionStep = styled.li`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const Bold = styled.span`
  font-weight: 600;
`

const FakeInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 19px;
  border-radius: 0;
`

const CopyButton = styled(Button)`
  &&& {
    color: ${style((theme) => theme.colors.stone)};
    font-weight: normal;
    border-radius: 0;
    margin-right: -20px;
  }
`

export const BigText = styled.span`
  font-size: 22px;
  font-weight: 500;
  color: ${style((theme) => theme.colors.slate)};
`

const TrackedCopyButton = withTrackClicked<any, ButtonProps>(() => ({
  object_name: 'directDepositOnlineCopy',
  screen: 'directDepositOnline',
  screen_name: 'directDepositOnline',
}))(CopyButton)

export const TrackedTitleLink = withTrackClicked((props) => ({
  object_name: 'directDepositOnlineBack',
  screen: 'directDepositOnline',
  screen_name: 'directDepositOnline',
  destination: 'directDepositSearch',
  cta_title: 'Edit',
  employer_name: props.employerName,
}))(PanelTitleLink)

export const CopyInput: React.SFC<{ text: string }> = ({ text }) => (
  <FakeInput>
    <Bodytext>{text}</Bodytext>

    <CopyToClipboard text={text}>
      <TrackedCopyButton type="flat" secondary={true} onPress={null}>
        Copy
      </TrackedCopyButton>
    </CopyToClipboard>
  </FakeInput>
)

const InfoIconContainer = styled.div`
  width: 24px;
  height: 24px;
  background-color: ${style((theme) => theme.colors.stone)};
  border-radius: 50%;
  margin-right: 10px;
`

const InfoImg = styled.img`
  width: 24px;
  height: 24px;
  padding: 3px;
`

const EmployerInfoContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  padding-bottom: 30px;
  margin: 0 20px 20px;
  align-items: center;
`

const EmployerText = styled(Bodytext)`
  color: ${style((theme) => theme.colors.stone)};
  font-size: 12px;
  line-height: 24px;
`

export const ProgressBar = styled<{ progress: number }, any>('div')`
  width: ${(p) => p.progress}%;
  height: 3px;
  background-color: ${style((theme) => theme.colors.green)};
  border-radius: 0 ${style((theme) => theme.styles.smallBorderRadius)}
    ${style((theme) => theme.styles.smallBorderRadius)} 0;
`

const ProgressBarInverse = styled<{ progress: number }, any>('div')`
  width: ${(p) => p.progress}%;
  height: 1px;
  background-color: ${style((theme) => theme.colors.ivory)};
  float: right;
  margin: 1px 0;
`

const ProgressBarHolder = styled<{ progress: number }, any>('div')`
  display: flex;
  align-items: stretch;
`

export const EmployerInfo = () => (
  <EmployerInfoContainer>
    <InfoIconContainer>
      <InfoImg src={InfoIconSVG} />
    </InfoIconContainer>
    <EmployerText>
      Log into your employer's portal to update your direct deposit.
    </EmployerText>
  </EmployerInfoContainer>
)

const SSNInfoTooltipContainer = styled.div`
  position: relative;
  display: inline-block;

  &:hover,
  &:focus {
    .ssn-tooltip-message {
      display: block;
    }
  }
`

const SSNInfoTooltipButton = styled.div`
  border: 1.5px solid ${style((theme) => theme.colors.stone)};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  color: ${style((theme) => theme.colors.stone)};
  margin-left: 10px;
  padding: 0;
  font-size: 11px;
  text-align: center;
  font-weight: bold;
  line-height: 18px;
`

const SSNInfoTooltipText = styled.div`
  position: absolute;
  font-size: 12px;
  line-height: 1.5;
  color: ${style((theme) => theme.colors.stone)};
  width: 332px;
  box-shadow: ${style((theme) => theme.shadows.lgMedium)};
  background-color: #ffffff;
  padding: 8px 16px;
  left: 40px;
  top: -17px;
  display: none;
  border-radius: 4px;

  &:before {
    content: '';
    display: inline-block;
    position: absolute;
    left: -6px;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid white;
    top: 20px;
    bottom: unset;
  }

  @media screen and (max-width: ${style((theme) => theme.breakpoints.md)}px) {
    width: calc(100vw - 110px);
    left: -95px;
    top: unset;
    bottom: 30px;

    &:before {
      content: '';
      left: 108px;
      border-right: 6px solid transparent;
      border-left: 6px solid transparent;
      border-top: 6px solid white;
      bottom: -6px;
      top: unset;
      border-bottom: unset;
    }
  }

  @media screen and (max-width: ${style((theme) => theme.breakpoints.sm)}px) {
    width: calc(100vw - 92px);
  }
`

export const SSNInfoTooltip = () => (
  <SSNInfoTooltipContainer>
    <SSNInfoTooltipButton>?</SSNInfoTooltipButton>
    <SSNInfoTooltipText className="ssn-tooltip-message">
      All of your data is protected with 256-bit encryption and never stored on
      your phone, tablet or computer.
    </SSNInfoTooltipText>
  </SSNInfoTooltipContainer>
)

export const ProgressBarContainer = (progressObj: { progress: number }) => (
  <ProgressBarHolder>
    <ProgressBar progress={progressObj.progress} />
    <ProgressBarInverse progress={100 - progressObj.progress} />
  </ProgressBarHolder>
)
